export type RecipientEngagementFilter = 'all' | 'opened' | 'bounced' | 'not_opened';

type EngagementFilterRecipient = {
  success: boolean;
  last_event?: string | null;
  opened?: boolean | null;
  opened_at?: string | null;
  bounced_at?: string | null;
};

export function recipientWasBounced(recipient: EngagementFilterRecipient): boolean {
  const event = recipient.last_event || '';
  return (
    event === 'bounced' ||
    event === 'complained' ||
    event === 'rejected' ||
    Boolean(recipient.bounced_at)
  );
}

export function recipientWasOpened(recipient: EngagementFilterRecipient): boolean {
  const event = recipient.last_event || '';
  return (
    recipient.opened === true ||
    event === 'opened' ||
    event === 'clicked' ||
    Boolean(recipient.opened_at)
  );
}

export function recipientMatchesEngagementFilter(
  recipient: EngagementFilterRecipient,
  filter: RecipientEngagementFilter
): boolean {
  if (filter === 'all') return true;

  const bounced = recipientWasBounced(recipient);
  const opened = recipientWasOpened(recipient);

  if (filter === 'opened') return recipient.success && opened && !bounced;
  if (filter === 'bounced') return bounced;
  if (filter === 'not_opened') return recipient.success && !opened && !bounced;
  return true;
}

export function recipientEngagementFilterCounts(
  recipients: EngagementFilterRecipient[]
): Record<RecipientEngagementFilter, number> {
  return {
    all: recipients.length,
    opened: recipients.filter((row) => recipientMatchesEngagementFilter(row, 'opened')).length,
    bounced: recipients.filter((row) => recipientMatchesEngagementFilter(row, 'bounced')).length,
    not_opened: recipients.filter((row) =>
      recipientMatchesEngagementFilter(row, 'not_opened')
    ).length,
  };
}

export function mergeRecipientEngagement<
  T extends { id: string; last_event?: string | null; opened?: boolean | null },
>(recipients: T[], engagement: T[]): T[] {
  if (engagement.length === 0) return recipients;
  const byId = new Map(engagement.map((row) => [row.id, row]));
  return recipients.map((row) => {
    const extra = byId.get(row.id);
    if (!extra) return row;
    return {
      ...row,
      last_event: extra.last_event ?? row.last_event,
      opened: extra.opened ?? row.opened,
    };
  });
}

export function bouncedRecipientEmails(
  recipients: Array<{
    recipient_email: string;
    last_event?: string | null;
    bounced_at?: string | null;
    success?: boolean;
  }>
): string[] {
  return recipients
    .filter((row) => recipientWasBounced({ success: row.success ?? true, ...row }))
    .map((row) => row.recipient_email);
}

export function emailsToCsv(emails: string[]): string {
  return ['email', ...emails].join('\n');
}

export type CampaignListStatus = 'opened' | 'delivered' | 'bounced' | 'failed';

export function campaignListStatus(campaign: {
  successful_sends: number;
  failed_sends: number;
  open_count?: number;
  bounce_count?: number;
}): CampaignListStatus {
  if ((campaign.bounce_count ?? 0) > 0) return 'bounced';
  if ((campaign.open_count ?? 0) > 0) return 'opened';
  if (campaign.failed_sends > 0 && campaign.successful_sends === 0) return 'failed';
  return 'delivered';
}
